import { useQuery, type UseQueryResult } from 'react-query';
import { fetchWindField, type WindFieldData } from '@/components/maps/wind/openMeteo';
import type { IDWStation } from '@/components/maps/wind/types';

/**
 * Fetch the multi-station Open-Meteo wind field once per session (shared
 * across the forecast + wildfire maps via react-query's cache). Pass
 * `enabled: false` to skip the network call until the layer is shown.
 */
export function useWindField(enabled: boolean = true): UseQueryResult<WindFieldData> {
  return useQuery<WindFieldData>('wind-field', fetchWindField, {
    enabled,
    staleTime: 15 * 60 * 1000, // 15 min — wind doesn't move that fast
    cacheTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });
}

/**
 * Pick the station samples for the hour closest to `timestamp`. Falls back to
 * the field's "current" hour when no/invalid timestamp is given.
 */
export function stationsForTimestamp(
  data: WindFieldData | undefined,
  timestamp?: string | null,
): IDWStation[] {
  if (!data) return [];
  const { hours, currentHourIndex, stationsByHour } = data;
  let idx = currentHourIndex;

  if (timestamp) {
    const target = new Date(timestamp).getTime();
    if (!Number.isNaN(target)) {
      let best = Infinity;
      for (let h = 0; h < hours.length; h++) {
        const diff = Math.abs(new Date(hours[h]!).getTime() - target);
        if (diff < best) {
          best = diff;
          idx = h;
        }
      }
    }
  }

  return stationsByHour[idx] ?? [];
}
